import { UploadIcon } from '@heroicons/react/outline';
import { useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { ArrowGlyph, CheckGlyph, tone } from '@/components/agents/primitives';
import Spinner from '@/components/Spinner';

import { INTERIOR_PAINT_COLORS } from '@/constants/interiorDesigner';
import { useUser } from '@/utils/useUser';

import { imageUrl } from './assets';
import {
  type StartOptions,
  useInteriorLandingT,
  usePageVars,
  useRoomVars,
  useStartInterior,
} from './kit';
import type { InteriorLandingPage } from './types';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 25 * 1024 * 1024;

/**
 * The hero's way in: a room photo (picked, dropped or one of ours), or no
 * photo at all. It's saved in this browser and the AI Interior Designer opens
 * with it, after sign-up when needed (see @/lib/interiorHandoff).
 */
export default function StartCard({ page }: { page: InteriorLandingPage }) {
  const { t } = useInteriorLandingT();
  const roomVars = useRoomVars();
  const vars = usePageVars(page);
  const { user } = useUser();
  const start = useStartInterior(page);
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [color, setColor] = useState(
    page.paint ? INTERIOR_PAINT_COLORS[2].label : undefined,
  );

  const go = async (key: string, options: StartOptions) => {
    if (busy) return;
    setBusy(key);
    setError('');
    try {
      await start({ prompt: color, ...options });
    } catch {
      setBusy(null);
    }
  };

  const pickFile = (file: File | undefined) => {
    if (!file || busy) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError(t('Please choose a JPG, PNG or WebP image.'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('This image is too large. Please use one under 25 MB.'));
      return;
    }
    void go('upload', { surface: 'upload', photo: { kind: 'file', file } });
  };

  return (
    <div>
      <div className='rounded-[26px] bg-white p-2 shadow-[0_24px_60px_-30px_rgba(22,12,90,0.45)] ring-1 ring-[#0f0f28]/[0.07] dark:bg-[#14141f] dark:ring-white/[0.08]'>
        {page.paint ? (
          <fieldset className='px-3 pb-3 pt-2.5'>
            <legend className={clsxm('text-sm font-semibold', tone.ink)}>
              {t('Pick a wall color')}
            </legend>
            <div className='mt-2.5 flex flex-wrap items-center gap-2'>
              {INTERIOR_PAINT_COLORS.map((paint) => {
                const selected = paint.label === color;
                return (
                  <button
                    key={paint.label}
                    type='button'
                    aria-pressed={selected}
                    aria-label={t(paint.label)}
                    title={t(paint.label)}
                    onClick={() => setColor(paint.label)}
                    className={clsxm(
                      'relative flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-inset ring-black/10 transition-transform duration-150 hover:scale-105',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                      selected &&
                        'scale-105 shadow-[0_0_0_2px_white,0_0_0_4px_rgb(var(--tw-color-primary-500))] dark:shadow-[0_0_0_2px_#14141f,0_0_0_4px_rgb(var(--tw-color-primary-400))]',
                    )}
                    style={{ backgroundColor: paint.hex }}
                  >
                    {selected ? (
                      <CheckGlyph
                        className={clsxm(
                          'h-4 w-4',
                          isDark(paint.hex) ? 'text-white' : 'text-[#10101c]',
                        )}
                      />
                    ) : null}
                  </button>
                );
              })}
              <span className={clsxm('ms-1 text-sm', tone.body)}>
                {color ? t(color) : null}
              </span>
            </div>
          </fieldset>
        ) : null}

        <button
          type='button'
          disabled={Boolean(busy)}
          onClick={() => fileInput.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            pickFile(event.dataTransfer.files?.[0]);
          }}
          className={clsxm(
            'group flex w-full items-center gap-4 rounded-[20px] border-2 border-dashed px-5 py-5 text-start transition sm:flex-col sm:px-6 sm:py-8 sm:text-center lg:flex-row lg:py-6 lg:text-start',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-wait',
            dragging
              ? 'border-primary-500 bg-primary-50/70 dark:border-primary-400 dark:bg-primary-500/10'
              : 'border-[#d9d9e6] bg-[#fafafd] hover:border-primary-400 hover:bg-primary-50/40 dark:border-white/15 dark:bg-white/[0.03] dark:hover:border-primary-400/60 dark:hover:bg-primary-500/[0.06]',
          )}
        >
          <span className='flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-primary-600 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_24px_-10px_rgba(79,70,229,0.8)] transition-transform duration-200 group-hover:-translate-y-0.5'>
            {busy === 'upload' ? (
              <Spinner className='h-5 w-5 text-white' />
            ) : (
              <UploadIcon className='h-6 w-6' aria-hidden />
            )}
          </span>
          <span className='min-w-0'>
            <span
              className={clsxm(
                'block text-base font-semibold sm:mt-1 lg:mt-0',
                tone.ink,
              )}
            >
              {busy === 'upload' ? t('Opening…') : t(page.uploadLabel, vars)}
            </span>
            <span className={clsxm('mt-0.5 block text-sm', tone.body)}>
              {t('Drop it here or')}{' '}
              <span className='font-semibold text-primary-600 underline decoration-primary-600/30 underline-offset-4 dark:text-primary-400'>
                {t('choose a file')}
              </span>
            </span>
            <span
              className={clsxm('mt-1.5 hidden text-xs sm:block', tone.quiet)}
            >
              {t('JPG, PNG or WebP. A phone photo is fine.')}
            </span>
          </span>
        </button>
        <input
          ref={fileInput}
          type='file'
          // No HEIC: iPhones then hand over a JPEG copy of the photo.
          accept='image/jpeg,image/png,image/webp'
          className='hidden'
          onChange={(event) => {
            pickFile(event.target.files?.[0]);
            event.target.value = '';
          }}
        />

        {error ? (
          <p
            role='alert'
            className='px-4 pt-2.5 text-sm text-rose-600 dark:text-rose-400'
          >
            {error}
          </p>
        ) : null}

        {page.samples.length > 0 ? (
          <div className='flex flex-wrap items-center gap-x-3 gap-y-2 px-3 pb-1.5 pt-3'>
            <span className={clsxm('text-sm', tone.body)}>
              {t('No photo? Try one of ours:')}
            </span>
            <div className='flex gap-2'>
              {page.samples.map((sample) => (
                <button
                  key={sample.source}
                  type='button'
                  disabled={Boolean(busy)}
                  title={t(sample.label)}
                  aria-label={t(
                    'Try the sample photo: {{room}}',
                    roomVars(sample.label),
                  )}
                  onClick={() =>
                    go(`sample:${sample.source}`, {
                      surface: 'sample',
                      // The photo's own room: a pool on the landscape page
                      // is not a backyard, a living room on the kitchen
                      // page is not a kitchen.
                      roomType: sample.roomType,
                      photo: {
                        kind: 'url',
                        url: sample.source,
                        label: t(sample.label),
                      },
                    })
                  }
                  className='relative h-11 w-14 flex-none overflow-hidden rounded-lg ring-1 ring-[#0f0f28]/10 transition duration-200 hover:-translate-y-0.5 hover:ring-2 hover:ring-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-wait dark:ring-white/15'
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={imageUrl(sample.image, 320)}
                    alt=''
                    width={56}
                    height={44}
                    loading='lazy'
                    decoding='async'
                    className='h-full w-full object-cover'
                  />
                  {busy === `sample:${sample.source}` ? (
                    <span className='absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-black/50'>
                      <Spinner className='h-4 w-4 text-primary-600' />
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {page.scratch ? (
          <button
            type='button'
            disabled={Boolean(busy)}
            onClick={() =>
              go('scratch', { surface: 'scratch', mode: 'generate' })
            }
            className={clsxm(
              'mt-1 flex w-full items-center justify-between gap-3 rounded-[18px] px-3.5 py-3 text-start text-sm transition-colors hover:bg-[#f4f4f8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 disabled:cursor-wait dark:hover:bg-white/[0.05]',
              tone.body,
            )}
          >
            <span>
              <span className={clsxm('font-semibold', tone.ink)}>
                {t('No photo at all?')}
              </span>{' '}
              {t(page.scratch)}
            </span>
            {busy === 'scratch' ? (
              <Spinner className='h-4 w-4 text-primary-600' />
            ) : (
              <ArrowGlyph className='h-4 w-4 text-primary-600 dark:text-primary-400' />
            )}
          </button>
        ) : null}
      </div>

      {user ? null : (
        <p className={clsxm('mt-4 text-center text-[13px]', tone.quiet)}>
          {t('Your first design is free. No credit card needed.')}
        </p>
      )}
    </div>
  );
}

/** Whether a paint swatch needs a light check mark on it. */
function isDark(hex: string) {
  const value = parseInt(hex.slice(1), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b < 140;
}
